import { PropsWithChildren } from "react";

import { LeagueNamingContextProvider } from "@/contexts";
import { useCurrentLeagueTourType } from "@/hooks";

/**
 * Mounts `LeagueNamingContext` for the league currently in URL scope, so every
 * naming-aware key under it resolves against that league's own `tourType`:
 * a `league` container says "league" / "stage", a `tour` container keeps
 * "circuit" / "tournament".
 *
 * Use this on league-scoped surfaces that render for BOTH container types and
 * have no league of their own to hand the naming provider — the league sidebar
 * panel, the league overview page, the league onboarding page. Pages that
 * already know their league (Create/Update) keep mounting
 * `LeagueNamingContextProvider` directly with the value they hold; a nested
 * provider wins, so there is no conflict.
 *
 * `tourType` is null outside a league scope and while the detail request is in
 * flight, which falls back to the context's own `"tour"` default — i.e. today's
 * wording — rather than briefly claiming a league.
 */
export const CurrentLeagueNamingProvider = ({ children }: PropsWithChildren) => {
  const { tourType } = useCurrentLeagueTourType();

  return (
    <LeagueNamingContextProvider naming={tourType ?? "tour"}>
      {children}
    </LeagueNamingContextProvider>
  );
};
